import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FinalizadoraProps, ListOption, SelectType } from './protocols';
import { InputContainer } from './styles';
import { Form, Spinner } from 'react-bootstrap';
import { useInfiniteQuery, useQueryClient } from 'react-query';
import { Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import api from '~/services/api';
import useDebounce from '~/hooks/useDebounce';
import { Lista } from './Lista';

const LIMIT = 10;

const orderByLabel = (options: ListOption[]) =>
  [...options].sort((a, b) =>
    a.label < b.label ? -1 : a.label > b.label ? 1 : 0,
  );

const buildCacheKey = (
  loja: number | number[] | null,
  filter: string,
  defaultLabel?: string,
) => {
  const def = defaultLabel ? `__def_${defaultLabel}` : '__def_none';
  if (Array.isArray(loja)) {
    const norm = [...loja].sort((a, b) => a - b).join('_');
    return `cache_finalizadoras_${norm}_${filter || 'ALL'}_${def}`;
  }
  return `cache_finalizadoras_${loja ?? 'none'}_${filter || 'ALL'}_${def}`;
};

export const Finalizadora: React.FC<FinalizadoraProps> = ({
  label = '',
  placeholder = '',
  name,
  register,
  isError,
  control,
  changeSelected,
  loja,
  retriever = null,
  defaultLabel,
  ...rest
}: FinalizadoraProps) => {
  const { debouncedFn } = useDebounce();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [selected, setSelected] = useState<ListOption>({
    label: undefined as unknown as string,
    value: undefined as unknown as number,
  });

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');

  const queryClient = useQueryClient();

  const filterFinalizadoras = useCallback(
    (options: any[]): any[] =>
      options.filter((item) =>
        item.finalizadora?.des_finalizadora
          ?.toLowerCase?.()
          ?.includes(filter.toLowerCase()),
      ),
    [filter],
  );

  const buildDefaultOption = useCallback((): ListOption | null => {
    if (!defaultLabel) return null;
    return {
      value: -1,
      label: defaultLabel,
      lojaId: undefined,
      isDefault: true,
    };
  }, [defaultLabel]);

  const fetchBySingleLoja = useCallback(
    async (ljId: number, pageParam = 1): Promise<any> => {
      const { data: dataFields } = await api.get(`/finalizadora-loja/${ljId}`, {
        params: { page: pageParam, limit: LIMIT, filter },
      });

      const options: ListOption[] = filterFinalizadoras(dataFields.data)
        .filter((d: any) => d.flg_inativa !== true)
        .map((dt: any) => ({
          value: dt.cod_finalizadora,
          label: `${dt.cod_finalizadora} - ${dt.finalizadora.des_finalizadora}`,
          lojaId: dt.cod_loja ?? ljId,
          ...dt,
        }));

      const optionsOrder = orderByLabel(options);
      const def = buildDefaultOption();
      const merged = def ? [def, ...optionsOrder] : optionsOrder;

      return {
        ...dataFields,
        data: merged,
        loja: ljId,
        grouped: false,
        lojas: [ljId],
      };
    },
    [filter, filterFinalizadoras, buildDefaultOption],
  );

  const fetchByMultiLojas = useCallback(
    async (lojas: number[], pageParam = 1): Promise<any> => {
      const def = buildDefaultOption();

      const responses = await Promise.all(
        lojas.map((lj) =>
          api
            .get(`/finalizadora-loja/${lj}`, {
              params: { page: pageParam, limit: LIMIT, filter },
            })
            .then((r) => ({ loja: lj, data: r.data }))
            .catch((err) => ({ loja: lj, error: err })),
        ),
      );

      const groupMap: Record<number, ListOption[]> = {};
      const flat: ListOption[] = [];

      responses.forEach((res) => {
        if ((res as any).error) {
          const msg =
            (res as any).error?.data?.message ?? String((res as any).error);
          toast.error(msg);
          groupMap[res.loja] = def ? [def] : [];
          if (def) flat.push(def);
          return;
        }

        const dataFields = (res as any).data;
        const options: ListOption[] = filterFinalizadoras(dataFields.data)
          .filter((d: any) => d.flg_inativa !== true)
          .map((dt: any) => ({
            value: dt.cod_finalizadora,
            label: `${dt.cod_finalizadora} - ${dt.finalizadora.des_finalizadora}`,
            lojaId: dt.cod_loja ?? res.loja,
            ...dt,
          }));

        const ordered = orderByLabel(options);
        const withDefault = def ? [def, ...ordered] : ordered;

        groupMap[res.loja] = withDefault;
        flat.push(...withDefault);
      });

      return {
        message: 'Registros encontrados',
        count: flat.length,
        data: flat,
        fields: [],
        pagination: { lastPage: 0, prevPage: 1, startPage: 1 },
        success: true,
        grouped: lojas.length > 1,
        lojas,
        groupMap,
      };
    },
    [filter, filterFinalizadoras, buildDefaultOption],
  );

  // Helper mínimo para aplicar retriever quando possível (1 loja e dropdown fechado)
  const maybeApplyRetriever = useCallback(
    (arr: ListOption[] | undefined, fallbackLojaId?: number) => {
      if (!retriever || isOpen || !Array.isArray(arr) || arr.length === 0)
        return;
      const opt = arr.find((o) => o.value === retriever && o.value !== -1);
      if (opt) {
        changeSelected({
          value: opt.value,
          label: opt.label,
          lojaId: opt.lojaId ?? fallbackLojaId,
        });
      }
    },
    [retriever, isOpen, changeSelected],
  );

  const fetchData = useCallback(
    async ({ pageParam = 1 }): Promise<any> => {
      try {
        if (!loja || (Array.isArray(loja) && loja.length === 0)) {
          const def = buildDefaultOption();
          return {
            count: def ? '1' : '0',
            data: def ? [def] : [],
            fields: [],
            pagination: { lastPage: 0, prevPage: 1, startPage: 1 },
            success: true,
            grouped: false,
            lojas: [],
          };
        }

        const cacheKey = buildCacheKey(loja, filter, defaultLabel);
        const cached = queryClient.getQueryData<any>(cacheKey);

        if (cached) {
          // Aplica retriever somente quando NÃO está agrupado (ou seja, 1 loja) e dropdown fechado
          if (cached.grouped === false) {
            const lojaId =
              cached.loja ??
              (Array.isArray(cached.lojas) ? cached.lojas[0] : undefined);
            maybeApplyRetriever(cached.data, lojaId);
          }
          return cached;
        }

        let payload: any;

        if (Array.isArray(loja)) {
          const lojas = [...loja].filter((n) => Number.isFinite(n));
          if (lojas.length === 1) {
            payload = await fetchBySingleLoja(lojas[0], pageParam);
          } else {
            payload = await fetchByMultiLojas(lojas, pageParam);
          }
        } else {
          payload = await fetchBySingleLoja(loja, pageParam);
        }

        // Após buscar, aplica retriever se for caso de 1 loja (não agrupado) e dropdown fechado
        if (payload?.grouped === false) {
          const lojaId =
            payload.loja ??
            (Array.isArray(payload.lojas) ? payload.lojas[0] : undefined);
          maybeApplyRetriever(payload.data, lojaId);
        }

        queryClient.setQueryData(cacheKey, payload);
        return payload;
      } catch (error: any) {
        if (error?.data?.message) {
          toast.error(error.data.message);
        } else {
          toast.error(String(error));
        }
        const def = buildDefaultOption();
        return {
          count: def ? '1' : '0',
          data: def ? [def] : [],
          fields: [],
          pagination: { lastPage: 0, prevPage: 1, startPage: 1 },
          success: true,
          grouped: false,
          lojas: [],
        };
      }
    },
    [
      filter,
      loja,
      defaultLabel,
      queryClient,
      fetchBySingleLoja,
      fetchByMultiLojas,
      buildDefaultOption,
      maybeApplyRetriever,
    ],
  );

  const { data, hasNextPage, fetchNextPage, isFetching, refetch } =
    useInfiniteQuery<any, unknown>(
      name,
      ({ pageParam = 1 }) => fetchData({ pageParam }),
      {
        refetchOnWindowFocus: false,
      },
    );

  useEffect(() => {
    // mudar loja OU defaultLabel invalida o cache da consulta
    queryClient.removeQueries(name);
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(loja), defaultLabel]);

  return (
    <InputContainer>
      <Form.Group>
        <Form.Label>{label}</Form.Label>
        <Controller
          name={name}
          control={control}
          defaultValue=""
          render={({ field: { value } }) => {
            if (inputRef?.current) {
              if (
                value?.label !== selected.label &&
                inputRef.current.placeholder !== placeholder
              ) {
                inputRef.current.placeholder = placeholder;
              }
              if (value?.label) {
                inputRef.current.placeholder = `${value.label}`;
              }
              if (!value?.label) {
                inputRef.current.placeholder = placeholder;
              }
            }

            return (
              <>
                <div className="select-container">
                  <label>
                    <input
                      type="text"
                      className={
                        isError ? 'form-control is-invalid' : 'form-control'
                      }
                      placeholder={placeholder}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        const next = event.target.value.toUpperCase();
                        event.target.value = next;
                        setFilter(next);
                        if (next === '') {
                          queryClient.removeQueries(name);
                        }
                        debouncedFn(() => refetch(), 500);
                      }}
                      autoComplete="off"
                      onClick={(event: any) => {
                        if (selected?.label) {
                          event.target.placeholder = selected.label;
                        }
                        event.target.value = '';
                        setIsOpen(true);
                      }}
                      ref={inputRef}
                      {...rest}
                    />
                    <div className="drop-indicator" aria-hidden>
                      <span role="img" aria-label="open">
                        {filter && isFetching ? (
                          <Spinner
                            animation="border"
                            size="sm"
                            className="spinner"
                          />
                        ) : (
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            role="presentation"
                          >
                            <path
                              d="M8.292 10.293a1.009 1.009 0 000 1.419l2.939 2.965c.218.215.5.322.779.322s.556-.107.769-.322l2.93-2.955a1.01 1.01 0 000-1.419.987.987 0 00-1.406 0l-2.298 2.317-2.307-2.327a.99.99 0 00-1.406 0z"
                              fill="currentColor"
                              fillRule="evenodd"
                            />
                          </svg>
                        )}
                      </span>
                    </div>
                  </label>
                </div>

                {isOpen && (
                  <Lista
                    inputRef={inputRef}
                    setIsOpen={setIsOpen}
                    setFilter={setFilter}
                    hasNextPage={hasNextPage}
                    fetchNextPage={fetchNextPage}
                    data={data}
                    selected={selected}
                    setSelected={setSelected}
                    isFetching={isFetching}
                    queryName={name}
                    changeSelected={(opt: SelectType) => {
                      changeSelected(opt);
                    }}
                    queryClient={queryClient}
                  />
                )}
              </>
            );
          }}
        />
      </Form.Group>
    </InputContainer>
  );
};
